import React, { useRef, useState } from 'react';
import {
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import styled from 'styled-components';
import useDashboardSelector from '../../../redux/selectorHooks/useDashboardSelector';
import { assertNotNil } from '../../../utils/types';
import { clipboardDashboardLink } from '../clipboardDashboardLink';
import CopyDashboardLink from '../CopyDashboardLink';
import KubitTooltip from '../../KubitTooltip';
import { PrimaryButton } from '../../Button';
import { FloatingLayer } from '../../FloatingManager';
import useDashboardReload from '../../../pages/DashboardRoute/utils/useDashboardReload';
import DashboardEntityPermissionsWidgetPopover from './DashboardEntityPermissionsWidgetPopover';
import { useAppDispatch } from '../../../redux/hooks';
import { reloadDashboardTemplates } from '../../../redux/slices/dashboards/dashboardInstance';

const Container = styled.div`
  display: grid;
  grid-template-columns: 24px auto;
  column-gap: 1px;
  height: 24px;
`;

const TextContainer = styled(PrimaryButton)`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: unset;
  height: 100%;
  padding: 0 6px;
  border: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const ShareDashboardPermissionsWidget = () => {
  const dispatch = useAppDispatch();
  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const { permission, baseId, id, isLatestInstance } = dashboard;
  const dashboardLink = clipboardDashboardLink(
    baseId,
    isLatestInstance ? '' : id,
  );
  const reloadDashboard = useDashboardReload(baseId);

  const [isOpen, setIsOpen] = useState(false);
  const hasPermissionChanges = useRef(false);

  const handleUpdatePermissions = async () => {
    // we need to perform full dashboard reload because entity permissions
    // change on the dashboard affect the report permissions retrieved
    // from their container (in this case - this dashboard) so the reports
    // will update their permissions too
    const updatedDashboard = await reloadDashboard();

    // Update permissions in templates
    if (updatedDashboard) {
      dispatch(reloadDashboardTemplates());
      hasPermissionChanges.current = false;
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      // If closing the popover and changes were made, reload the dashboard
      if (!open && hasPermissionChanges.current) {
        handleUpdatePermissions();
      }
      setIsOpen(open);
    },
    middleware: [offset(2), shift()],
    placement: 'bottom-end',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: true,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handlePermissionsChanged = (hasChanges: boolean) => {
    hasPermissionChanges.current = hasChanges;
  };

  return (
    <>
      <Container>
        <CopyDashboardLink dashboardLink={dashboardLink} />
        <KubitTooltip
          content={'User Access'}
          placement="bottom-end"
          disabled={isOpen}
          noOpenDelay={true}
        >
          <TextContainer ref={refs.setReference} {...getReferenceProps()}>
            Share
          </TextContainer>
        </KubitTooltip>
      </Container>
      {isOpen && (
        <FloatingLayer>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <DashboardEntityPermissionsWidgetPopover
              permission={permission}
              dashboardBaseId={baseId}
              onPermissionsChanged={handlePermissionsChanged}
            />
          </div>
        </FloatingLayer>
      )}
    </>
  );
};

export default ShareDashboardPermissionsWidget;
